import React, { useEffect, useRef, useState } from 'react';
import useImpersonation from '../../hooks/useImpersonation';
import PhiFriendlyNote from '../../components/PhiFriendlyNote.jsx';

const API = process.env.REACT_APP_API_URL;

export default function CapNew({ user, onCreated, onCancel }) {
  // Belt-and-suspenders: while a platform_superuser is impersonating a client
  // org, the server returns 403 on any write — but we also disable the submit
  // affordance up front so the UX is honest. Pattern: const { active } = useImpersonation();
  const { active: impersonating } = useImpersonation();
  const [title, setTitle] = useState('');
  const [deficiency, setDeficiency] = useState('');
  const [urgency, setUrgency] = useState('medium');
  const [dueDate, setDueDate] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [primaryOwnerId, setPrimaryOwnerId] = useState('');

  const [departments, setDepartments] = useState([]);
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [cfrRefs, setCfrRefs] = useState([]);
  const [newCfr, setNewCfr] = useState('');

  // Source document attached on this screen (held in memory until the CAP is
  // created, then uploaded to the post-create endpoint). The AI analyzes the
  // file as soon as it's chosen and prefills the form below — the user edits
  // anything they don't like before clicking Create CAP.
  const [sourceFile, setSourceFile] = useState(null);
  const [sourceAnalyzing, setSourceAnalyzing] = useState(false);
  const [sourceAnalyzed, setSourceAnalyzed] = useState(false);
  const [sourceMessage, setSourceMessage] = useState(null);
  const fileInputRef = useRef(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Defense-in-depth gate: even if a stale CapList bundle showed the "+ New
  // CAP" button, the form itself refuses to render unless the server confirms
  // create rights. The server POST /api/pcap/caps will also 403 — this is a
  // belt over that belt. null = unknown (loading), true = allowed, false = no.
  const [canCreateAuth, setCanCreateAuth] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/pcap/access/can-create`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setCanCreateAuth(d?.success ? !!d.allowed : false))
      .catch(() => setCanCreateAuth(false));
  }, []);

  const addCfr = (e) => {
    e?.preventDefault?.();
    const v = newCfr.trim();
    if (!v) return;
    if (cfrRefs.includes(v)) { setNewCfr(''); return; }
    setCfrRefs([...cfrRefs, v]);
    setNewCfr('');
  };
  const removeCfr = (ref) => setCfrRefs(cfrRefs.filter(r => r !== ref));

  useEffect(() => {
    fetch(`${API}/api/pcap/departments`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (d.success) setDepartments(d.departments || []); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (departmentId) params.set('department_id', departmentId);
    fetch(`${API}/api/pcap/eligible-users?${params.toString()}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (d.success) setEligibleUsers(d.users || []); })
      .catch(() => {});
  }, [departmentId]);

  // Pick a source document → send it to the analyze endpoint → prefill the
  // form with what the AI saw. The file itself is held in `sourceFile` state
  // and attached after CAP creation via /caps/:id/source-document/upload.
  const handleSourceFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSourceFile(file);
    setSourceAnalyzed(false);
    setSourceMessage(null);
    setSourceAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/api/pcap/source-document/analyze`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Analysis failed');

      const s = data.suggestions || {};
      // Only prefill blank fields — never overwrite something the user typed.
      setTitle(prev => prev.trim() ? prev : (s.title || ''));
      setDeficiency(prev => prev.trim() ? prev : (s.deficiency || ''));
      setUrgency(prev => prev && prev !== 'medium' ? prev : (s.urgency || 'medium'));
      if (Array.isArray(s.cfr_refs) && s.cfr_refs.length) {
        setCfrRefs(prev => {
          const merged = [...prev];
          s.cfr_refs.forEach(r => { if (!merged.includes(r)) merged.push(r); });
          return merged;
        });
      }
      setSourceAnalyzed(true);
      setSourceMessage(data.text_extracted
        ? 'Document analyzed. Edit anything below before saving.'
        : (data.message || 'Document attached. AI could not extract text — fill in the fields manually.'));
    } catch (err) {
      setSourceMessage(`Could not analyze the document: ${err.message}. You can still create the CAP and the file will be attached.`);
    } finally {
      setSourceAnalyzing(false);
    }
  };

  const clearSourceFile = () => {
    setSourceFile(null);
    setSourceAnalyzed(false);
    setSourceMessage(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const cap = {
        title: title.trim(),
        deficiency: deficiency.trim() || null,
        urgency,
        due_date: dueDate || null,
        source_type: sourceFile ? 'audit_report' : 'manual',
        department_id: departmentId || null,
        cfr_refs: cfrRefs,
      };
      const res = await fetch(`${API}/api/pcap/caps`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cap),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to create');

      const newCapId = data.cap.id;

      // Attach the source document to the freshly-created CAP. Any failure
      // here doesn't roll back the CAP — the user can re-upload from the
      // detail page's Source-document section.
      if (sourceFile) {
        try {
          const fd = new FormData();
          fd.append('file', sourceFile);
          await fetch(`${API}/api/pcap/caps/${newCapId}/source-document/upload`, {
            method: 'POST',
            credentials: 'include',
            body: fd,
          });
        } catch { /* non-fatal — surfaced on the detail page */ }
      }

      if (primaryOwnerId) {
        try {
          await fetch(`${API}/api/pcap/caps/${newCapId}/milestones`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              kind: 'corrective',
              description: 'Primary owner assignment',
              owner_user_id: primaryOwnerId,
            }),
          });
        } catch { /* non-fatal */ }
      }

      onCreated(newCapId);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (canCreateAuth === null) {
    return (
      <div className="max-w-4xl mx-auto px-6 pt-10 pb-16 text-sm text-slate-400">Loading…</div>
    );
  }
  if (canCreateAuth === false) {
    return (
      <div className="max-w-4xl mx-auto px-6 pt-10 pb-16">
        <button
          onClick={onCancel}
          className="text-sm text-slate-500 hover:text-slate-900 mb-6 inline-flex items-center gap-1.5 transition"
        >
          ← Back
        </button>
        <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-8 text-center">
          <h2 className="text-[17px] font-semibold text-slate-900 mb-1">You don't have access to create CAPs</h2>
          <p className="text-[13px] text-slate-500 leading-relaxed">
            Creating a CAP requires compliance access or an explicit grant from your compliance team.
            Reach out to compliance if you need this — they can grant access from within the CAP module.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 pt-10 pb-16">
      <button
        onClick={onCancel}
        className="text-sm text-slate-500 hover:text-slate-900 mb-6 inline-flex items-center gap-1.5 transition"
      >
        ← Back
      </button>

      <h1 className="text-[26px] font-semibold text-slate-900 tracking-tight mb-1.5">New corrective action plan</h1>
      <p className="text-sm text-slate-500 mb-7">
        Start with the audit report or finding letter — the AI will read it and prefill the form.
        You can also skip the upload and fill the form manually.
      </p>

      {/* Source document — sits ABOVE the form on purpose: this is the input
          that decides what the rest of the form gets prefilled with. */}
      <div className="bg-white rounded-2xl ring-1 ring-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-6 sm:p-7 mb-5">
        <div className="flex items-start justify-between gap-4 mb-2">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">Source document</h2>
            <p className="text-xs text-slate-500 mt-0.5">PDF, DOCX, or XLSX. Optional but recommended — the AI uses it to draft the description.</p>
          </div>
        </div>

        {!sourceFile && <PhiFriendlyNote variant="upload" className="mt-3" />}

        {!sourceFile && (
          <label className="mt-3 flex items-center justify-center gap-2 px-4 py-6 rounded-xl border-2 border-dashed border-slate-300 hover:border-slate-400 cursor-pointer transition text-sm text-slate-600">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.xlsx,.doc,.xls"
              onChange={handleSourceFileChange}
              className="hidden"
            />
            <span>📎 Choose audit report or finding letter</span>
          </label>
        )}

        {sourceFile && (
          <div className="mt-3 flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-lg bg-slate-50 ring-1 ring-slate-200">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-base">📄</span>
              <div className="min-w-0">
                <div className="text-sm text-slate-800 truncate">{sourceFile.name}</div>
                <div className="text-[11px] text-slate-500">
                  {(sourceFile.size / 1024).toFixed(0)} KB
                  {sourceAnalyzing && ' · Analyzing…'}
                  {sourceAnalyzed && !sourceAnalyzing && ' · AI suggestions applied'}
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={clearSourceFile}
              className="text-xs text-slate-500 hover:text-red-600 px-2 py-1 transition"
              disabled={sourceAnalyzing}
            >
              Remove
            </button>
          </div>
        )}

        {sourceMessage && (
          <p className="mt-2.5 text-[12px] text-slate-600">{sourceMessage}</p>
        )}
      </div>

      {error && (
        <div className="rounded-xl bg-red-50 ring-1 ring-red-200 p-3 text-sm text-red-800 mb-4">
          {error}
        </div>
      )}

      <form onSubmit={submit} className="bg-white rounded-2xl ring-1 ring-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,0.04)] p-6 sm:p-7 space-y-5">
        <div>
          <label className="block text-sm font-medium text-slate-800 mb-1">Title</label>
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="A short name for this CAP"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-800 mb-1">What happened</label>
          <textarea
            value={deficiency}
            onChange={e => setDeficiency(e.target.value)}
            placeholder="Describe the deficiency in plain language."
            rows={6}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-800 mb-1">CFR or chapter references (optional)</label>
          <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-slate-200 px-2 py-1.5 min-h-[2.5rem]">
            {cfrRefs.length === 0 && (
              <span className="text-[12px] text-slate-400 italic mr-1">None yet.</span>
            )}
            {cfrRefs.map(ref => (
              <span
                key={ref}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 ring-1 ring-slate-200 text-[12px] text-slate-700"
              >
                {ref}
                <button type="button" onClick={() => removeCfr(ref)} className="text-slate-400 hover:text-slate-700" aria-label={`Remove ${ref}`}>×</button>
              </span>
            ))}
            <input
              value={newCfr}
              onChange={e => setNewCfr(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addCfr(e); }}
              placeholder="42 CFR 422.504"
              className="flex-1 min-w-[8rem] text-[12px] border-0 focus:outline-none focus:ring-0"
            />
            <button type="button" onClick={addCfr} disabled={!newCfr.trim()} className="text-[12px] text-slate-600 hover:text-slate-900 disabled:opacity-50">Add</button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-800 mb-1">Department</label>
            <select
              value={departmentId}
              onChange={e => { setDepartmentId(e.target.value); setPrimaryOwnerId(''); }}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
            >
              <option value="">Org-wide (no specific department)</option>
              {departments.map(d => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
            <p className="text-[11px] text-slate-500 mt-1">Department-bound users only see CAPs in their department.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-800 mb-1">Primary owner (optional)</label>
            <select
              value={primaryOwnerId}
              onChange={e => setPrimaryOwnerId(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
            >
              <option value="">No primary owner yet</option>
              {eligibleUsers.length === 0 && (
                <option disabled>No users with CAP access</option>
              )}
              {eligibleUsers.map(u => (
                <option key={u.id} value={u.id}>
                  {u.name} {u.cap_role ? `(${u.cap_role})` : ''}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-slate-500 mt-1">Pulled from your onboarded team.</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-800 mb-1">Urgency</label>
            <select
              value={urgency}
              onChange={e => setUrgency(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-800 mb-1">Due date (optional)</label>
            <input
              type="date"
              value={dueDate}
              onChange={e => setDueDate(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3.5 py-2 rounded-lg text-sm text-slate-700 hover:bg-slate-100 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || sourceAnalyzing || !title.trim() || impersonating}
            title={impersonating ? 'Read-only during impersonation' : ''}
            className="px-3.5 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Creating…' : 'Create CAP'}
          </button>
        </div>
      </form>
    </div>
  );
}
